import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Kit cards on the bartender-kit landing and the home page (sections/kit-chooser.liquid).
 * Each card is a plain cart form, so it works without this script. The script marks the card once
 * the visitor picks a colour (the card then shows that colour's price instead of «від …»), adds the
 * chosen colour to the cart without leaving the page, tells the cart drawer and the cart icon about
 * it the same way the product form does (assets/product-form.js) and opens the cart drawer.
 */
class KitChooser extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.name === 'id') input.form?.toggleAttribute('data-picked', true);
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      // The drawer comes back in the language of sections_url; without it, Ukrainian on /ru
      formData.append('sections_url', window.location.pathname);
    }

    this.#showError(form, '');
    button?.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    let added = false;
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();
      const cart = await this.#fetchCart();
      const detail = {
        items: cart.items,
        source: 'kit-chooser',
        sourceId: form.id,
        itemCount: 1,
        didError: Boolean(data.status),
        sections: data.status ? undefined : data.sections,
      };

      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail });

      if (data.status) {
        form.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        this.#showError(form, data.description || data.message || '');
      } else {
        if (button instanceof HTMLElement) this.#showAdded(form, button);
        added = true;
      }
    } catch (error) {
      deferred.reject(error);
      // Something went wrong with the request: fall back to the ordinary form submit
      form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
    }

    if (added) {
      // After the cart drawer has taken the new contents from the response
      await deferred.promise;
      openCartDrawer();
    }
  };

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (cartItems && typeof cartItems.fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  /**
   * The button says the kit is in the cart: the only sign of it when the cart is a page, not a drawer
   * @param {HTMLFormElement} form
   * @param {HTMLElement} button
   */
  #showAdded(form, button) {
    const status = form.querySelector('[data-added-message]');
    if (status instanceof HTMLElement) status.textContent = status.dataset.addedMessage ?? '';

    button.dataset.added = '';
    clearTimeout(Number(button.dataset.addedTimer));
    button.dataset.addedTimer = String(
      setTimeout(() => {
        delete button.dataset.added;
        if (status instanceof HTMLElement) status.textContent = '';
      }, 2000)
    );
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  #showError(form, message) {
    const error = form.querySelector('.kit-card__error');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
    error.hidden = !message;
  }
}

/**
 * Opens the cart drawer, so that the buyer sees the kit in the cart. The theme setting that opens
 * the drawer on every add stays off: it would open on each «+» of the quick-order shelves too.
 */
function openCartDrawer() {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (typeof drawer?.open === 'function') drawer.open();
}

if (!customElements.get('kit-chooser')) {
  customElements.define('kit-chooser', KitChooser);
}
